import { ApprovalInboxResponse } from "@/api/ApprovalInboxResponse";
import { useAuthContext } from "@/auth/AuthContext";
import {
  ApprovalInboxSummary,
  emptyApprovalInboxSummary,
  summarizeApprovalInbox,
} from "@/data/ApprovalInboxUtil";
import { OrgUtil } from "@/data/OrgUtil";
import { approvalInboxService } from "@/service/ApprovalInboxService";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export const emptyApprovalInbox = (): ApprovalInboxResponse => ({
  pendingTimesheets: [],
  pendingAbsences: [],
  rejectedTimesheets: [],
  rejectedAbsences: [],
});

const cloneApprovalInbox = (inbox: ApprovalInboxResponse): ApprovalInboxResponse => ({
  pendingTimesheets: inbox.pendingTimesheets.concat([]),
  pendingAbsences: inbox.pendingAbsences.concat([]),
  rejectedTimesheets: inbox.rejectedTimesheets.concat([]),
  rejectedAbsences: inbox.rejectedAbsences.concat([]),
});

interface ApprovalInboxContextValue {
  inbox: ApprovalInboxResponse;
  summary: ApprovalInboxSummary;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setInbox: Dispatch<SetStateAction<ApprovalInboxResponse>>;
}

const ApprovalInboxContext = createContext<ApprovalInboxContextValue | undefined>(undefined);

export const ApprovalInboxProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthContext();
  const [inbox, setInbox] = useState<ApprovalInboxResponse>(emptyApprovalInbox);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const shouldLoad = !!user && !OrgUtil.isRoleExtern(user.data.role);
  const userId = user?.data.id;
  const organizationId = user?.data.organizationId;
  const token = user?.token;

  const refresh = useCallback(async (): Promise<void> => {
    const version = ++requestVersion.current;
    if (!shouldLoad) {
      setInbox(emptyApprovalInbox());
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await approvalInboxService.fetchInbox();
      if (version !== requestVersion.current) return;
      setInbox(cloneApprovalInbox(response));
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("Failed to load approval inbox", err);
      setError(err instanceof Error ? err.message : "Approval inbox could not be loaded.");
    } finally {
      if (version === requestVersion.current) setIsLoading(false);
    }
  }, [shouldLoad, userId, organizationId, token]);

  useEffect(() => {
    setInbox(emptyApprovalInbox());
    refresh();
    return () => { requestVersion.current++; };
  }, [refresh]);

  const summary = useMemo(() => summarizeApprovalInbox(inbox), [inbox]);
  const value = useMemo<ApprovalInboxContextValue>(() => ({
    inbox,
    summary,
    isLoading,
    error,
    refresh,
    setInbox,
  }), [error, inbox, isLoading, refresh, summary]);

  return (
    <ApprovalInboxContext.Provider value={value}>
      {children}
    </ApprovalInboxContext.Provider>
  );
};

export const useApprovalInbox = (): ApprovalInboxContextValue => {
  const context = useContext(ApprovalInboxContext);
  if (!context) {
    throw new Error("useApprovalInbox must be used within an ApprovalInboxProvider");
  }
  return context;
};
